import React, { useState, useEffect } from "react";
import Button from "react-bootstrap/Button";
import DataTable from "react-data-table-component";
import { useNavigate } from "react-router-dom";
import { getProducts, updateProducts } from "../../../../apis/products";
import { toastify } from "../../../../helperFunctions/toastify";
import {
	customNumberSort,
	customStringSort,
	exportData,
} from "../../../../helperFunctions/utils";
import DeleteModal from "../../../SharedComponents/DeleteModal";
import FilterComponent from "../../../SharedComponents/FilterComponent";

export default function Products() {
	const navigate = useNavigate();
	const [products, setProducts] = useState([]);
	const [filteredData, setFilteredData] = useState([]);
	const [show, setShow] = useState(false);
	const [deleting, setDeleting] = useState("");

	const handleClose = () => {
		setShow(false);
		setDeleting("");
	};

	const populateData = async () => {
		const response = await getProducts();
		if (response?.status !== "Fail") {
			setProducts(response.reverse());
		} else {
			if (response?.message === "No data Found") {
				setProducts([]);
			} else {
				toastify(response?.message, "error");
			}
		}
	};

	const removeProduct = async () => {
		setShow(false);
		let response = await updateProducts(deleting, { isDeleted: true });
		if (response?.status !== "Fail") {
			populateData();
			toastify("Product deleted successfully!", "success");
		} else {
			toastify(response?.message, "error");
		}
	};

	const editProduct = (e) => {
		navigate("/add-inventory-products", {
			state: {
				product: e,
			},
		});
	};

	const addProduct = () => {
		navigate("/add-inventory-products");
	};

	useEffect(() => {
		populateData();
	}, []);

	const columns = [
		{
			name: (
				<div className="d-flex align-items-center" id="ovly25">
					<span className="me-2">Code</span>
					{
						<FilterComponent
							type="search"
							array={products}
							setArray={setFilteredData}
							value="Code"
						/>
					}
				</div>
			),
			selector: (row) => row.code,
			wrap: true,
			sortable: true,
			width: "160px",
			sortFunction: (a, b) => customStringSort(a, b, "code"),
		},
		{
			name: (
				<div className="d-flex align-items-center" id="ovly26">
					<span className="me-2">Name</span>
					{
						<FilterComponent
							type="search"
							array={products}
							setArray={setFilteredData}
							value="Name"
						/>
					}
				</div>
			),
			selector: (row) => row.name,
			wrap: true,
			width: "200px",
			sortable: true,
			sortFunction: (a, b) => customStringSort(a, b, "name"),
		},
		{
			name: "UOM",
			selector: (row) => row.uom.name,
			wrap: true,
			sortable: true,
			sortFunction: (a, b) => customStringSort(a, b, "uom.name", true),
		},
		{
			name: (
				<div className="d-flex align-items-center" id="ovly27">
					<span className="me-2">Type</span>
					{
						<FilterComponent
							type="radio"
							array={products}
							setArray={setFilteredData}
							options={products.map((e) => e.type.name)}
							value="ProductType"
						/>
					}
				</div>
			),
			selector: (row) => row.type.name,
			wrap: true,
			sortable: true,
			sortFunction: (a, b) => customStringSort(a, b, "type.name", true),
		},
		{
			name: "Qty",
			selector: (row) => row.quantity?.toLocaleString(),
			wrap: true,
			sortable: true,
			sortFunction: (a, b) => customNumberSort(a, b, "quantity"),
		},
		{
			name: "Average Unit Cost",
			selector: (row) => row.averageUnitCost?.toLocaleString(),
			wrap: true,
			width: "170px",
			sortable: true,
			sortFunction: (a, b) => customNumberSort(a, b, "averageUnitCost"),
		},
		{
			name: "Unit Selling Price",
			selector: (row) => row.unitSellingPrice?.toLocaleString(),
			wrap: true,
			width: "170px",
			sortable: true,
			sortFunction: (a, b) => customNumberSort(a, b, "unitSellingPrice"),
		},
		{
			name: "Total Selling Price",
			selector: (row) => row.totalSellingPrice?.toLocaleString(),
			wrap: true,
			width: "170px",
			sortable: true,
			sortFunction: (a, b) => customNumberSort(a, b, "totalSellingPrice"),
		},
		{
			name: "Action",
			selector: (row) => row.action,
			width: "180px",
		},
	];

	const data = (filteredData.length === 0 ? products : filteredData)?.map(
		(e, index) => {
			return {
				index: index,
				id: e._id,
				code: e.code,
				name: e.name,
				uom: e.uom,
				quantity: e.qty,
				type: e.type,
				averageUnitCost: e.averageUnitCost,
				unitSellingPrice: e.unitSellingPrice,
				totalSellingPrice: e.totalSellingPrice,
				action: (
					<div className="d-flex">
						<div
							className="mx-3  cursor-pointer icon-width"
							onClick={() => {
								editProduct(e);
							}}
						>
							<i className="fa fa-pen"></i>
						</div>
						<div
							className="mx-3  cursor-pointer icon-width"
							onClick={() => {
								setDeleting(e._id);
								setShow(true);
							}}
						>
							<i className="fa fa-trash"></i>
						</div>
						<div
							className="mx-3  cursor-pointer icon-width"
							onClick={() =>
								navigate("/product-details", {
									state: {
										product: e,
									},
								})
							}
						>
							<i class="fa-regular fa-eye"></i>
						</div>
					</div>
				),
			};
		},
	);

	const customStyles = {
		headCells: {
			style: {
				justifyContent: "center",
			},
		},
		cells: {
			style: {
				justifyContent: "center",
				textAlign: "center",
			},
		},
	};

	const conditionalRowStyles = [
		{
			when: (row) => row,
			style: {
				borderWidth: "0px",
			},
		},
		{
			when: (row) => row.index % 2 === 1,
			style: {
				borderWidth: "0px",
				backgroundColor: "#f8f8f8",
			},
		},
	];

	return (
		<>
			<div className="container p-0">
				<div className="mainHeading px-4">
					<h1>Inventory / Products</h1>
					<div className="d-flex">
						<Button
							className="addItem me-3"
							onClick={() => {
								const tempDataArr = data?.map((e) => {
									return {
										Code: e.code,
										Name: e.name,
										UOM: e?.uom?.name,
										Quantity: e.quantity,
										Type: e?.type?.name,
										"Average Unit Cost": e.averageUnitCost,
										"Unit Selling Price":
											e.unitSellingPrice,
										"Total Selling Price":
											e.totalSellingPrice,
									};
								});
								exportData(tempDataArr, "Products");
							}}
						>
							Export
						</Button>
						<Button className="addItem" onClick={addProduct}>
							+ New Product
						</Button>
					</div>
				</div>

				<DeleteModal
					content="Confirm Product Deletion?"
					onYes={removeProduct}
					onNo={handleClose}
					show={show}
				/>

				<DataTable
					columns={columns}
					customStyles={customStyles}
					data={data || []}
					pagination
					conditionalRowStyles={conditionalRowStyles}
				/>
			</div>
		</>
	);
}
